import { Link } from "react-router-dom";

export type ArticleItem = {
  id: string;
  slug: string;
  title: string;
  categorySlug: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: number;
  views: number;
  hot: boolean;
  tags: string[];
  excerpt: string;
  cover: string;
  content: string[];
};

type ArticleCardProps = {
  article: ArticleItem;
  variant?: "default" | "wide" | "compact";
  index?: number;
};

const formatViews = (views: number) => {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
};

export default function ArticleCard({ article, variant = "default", index }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 border-b border-background-200 py-3.5 last:border-b-0"
      >
        <span className="label-font mt-0.5 w-6 shrink-0 text-sm font-semibold text-primary-500">
          {typeof index === "number" ? String(index + 1).padStart(2, "0") : ""}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span>{article.date}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group card-lift overflow-hidden rounded-lg border border-background-200 bg-background-100/60 sm:flex">
        <Link
          to={`/article/${article.id}`}
          className="block h-44 w-full shrink-0 overflow-hidden sm:h-auto sm:w-56 md:w-64"
        >
          <img
            src={article.cover}
            alt={`${article.title} - 独家黑料速递配图`}
            title={`${article.title} · 料闻社速递`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-center p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.categorySlug}`}
              className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {article.categoryName}
            </Link>
            {article.hot && (
              <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                高热
              </span>
            )}
          </div>
          <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.excerpt}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-timer-line" />
              {article.readTime} 分钟
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group card-lift flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100/60">
      <Link to={`/article/${article.id}`} className="block h-44 w-full overflow-hidden md:h-48">
        <img
          src={article.cover}
          alt={`${article.title} - 独家黑料速递配图`}
          title={`${article.title} · 料闻社速递`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/category/${article.categorySlug}`}
            className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {article.categoryName}
          </Link>
          {article.hot && (
            <span className="flex items-center gap-1 rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
              <i className="ri-fire-line" />
              高热
            </span>
          )}
        </div>
        <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-3.5 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.date}
          </span>
        </div>
      </div>
    </article>
  );
}